import * as THREE from 'three';

/**
 * FIKO ULTRA PERFORMANCE MANAGER
 * ================================
 * Milyonlarca CAD objesi icin her kosulda muntazam performans
 *
 * HEDEFLER:
 * - 60 FPS @ 10M+ triangle
 * - <3ms hover response
 * - <16ms frame budget
 * - Zero GC pause
 *
 * STRATEJILER:
 * 1. Adaptive Quality System - FPS'e gore kalite ayarla
 * 2. Predictive Rendering - Bir sonraki frame'i onceden hesapla
 * 3. Hierarchical Spatial Index - O(1) hover lookup
 * 4. Frame Budget Enforcement - Asla 16ms'i gecme
 * 5. GPU-First Architecture - CPU'yu bypass et
 */

// ==================== TYPES ====================

export interface PerformanceConfig {
  targetFPS: number;
  minAcceptableFPS: number;
  frameBudgetMs: number;
  hoverBudgetMs: number;
  renderBudgetMs: number;

  // Quality levels
  qualityLevels: QualityLevel[];
  currentQualityIndex: number;

  // Thresholds
  triangleCountThresholds: {
    low: number;      // < 100K - full quality
    medium: number;   // 100K - 1M - medium quality
    high: number;     // 1M - 10M - low quality
    extreme: number;  // > 10M - minimal quality
  };
}

export interface QualityLevel {
  name: string;
  lodBias: number;           // 0 = full detail, higher = more aggressive LOD
  shadowsEnabled: boolean;
  ssaoEnabled: boolean;
  edgeRenderingQuality: 'full' | 'simplified' | 'minimal' | 'off';
  hoverHighlightQuality: 'full' | 'fast' | 'minimal';
  maxVisibleObjects: number;
  frustumCullingGranularity: 'fine' | 'coarse' | 'aggressive';
  occlusionCullingEnabled: boolean;
  antialiasingLevel: 0 | 2 | 4 | 8;
  textureQuality: 'full' | 'half' | 'quarter';
}

export interface FrameMetrics {
  frameTime: number;
  fps: number;
  renderTime: number;
  hoverTime: number;
  cullingTime: number;
  updateTime: number;
  gpuTime: number;
  trianglesRendered: number;
  drawCalls: number;
  objectsVisible: number;
  objectsCulled: number;
}

export interface HoverPrediction {
  predictedX: number;
  predictedY: number;
  velocity: { x: number; y: number };
  confidence: number;
  precomputedResult: any | null;
}

// ==================== HIERARCHICAL SPATIAL INDEX ====================

/**
 * 4-Level Hierarchical Grid for O(1) hover lookup
 * Level 0: 25px cells (finest)
 * Level 1: 100px cells
 * Level 2: 400px cells
 * Level 3: 1600px cells (coarsest)
 */
export class FikoHierarchicalSpatialIndex {
  private levels: Map<string, Set<number>>[] = [];
  private cellSizes: number[] = [25, 100, 400, 1600];
  private objectBounds: Map<number, { minX: number; minY: number; maxX: number; maxY: number; depth: number }>
    = new Map();

  private screenWidth: number = 1920;
  private screenHeight: number = 1080;

  // Pre-allocated arrays for query results
  private queryResultPool: number[] = new Array(1000).fill(0);
  private queryResultCount: number = 0;

  constructor() {
    this.initializeLevels();
  }

  private initializeLevels(): void {
    for (let i = 0; i < this.cellSizes.length; i++) {
      this.levels.push(new Map());
    }
  }

  /**
   * Update screen dimensions
   */
  setScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
  }

  /**
   * Add object to spatial index
   */
  addObject(
    objectId: number,
    screenMinX: number,
    screenMinY: number,
    screenMaxX: number,
    screenMaxY: number,
    depth: number
  ): void {
    // Store bounds
    this.objectBounds.set(objectId, {
      minX: screenMinX,
      minY: screenMinY,
      maxX: screenMaxX,
      maxY: screenMaxY,
      depth
    });

    // Add to each level
    for (let level = 0; level < this.cellSizes.length; level++) {
      const cellSize = this.cellSizes[level];
      const startCellX = Math.floor(screenMinX / cellSize);
      const endCellX = Math.floor(screenMaxX / cellSize);
      const startCellY = Math.floor(screenMinY / cellSize);
      const endCellY = Math.floor(screenMaxY / cellSize);

      for (let cx = startCellX; cx <= endCellX; cx++) {
        for (let cy = startCellY; cy <= endCellY; cy++) {
          const key = `${cx},${cy}`;
          if (!this.levels[level].has(key)) {
            this.levels[level].set(key, new Set());
          }
          this.levels[level].get(key)!.add(objectId);
        }
      }
    }
  }

  /**
   * Remove object from index
   */
  removeObject(objectId: number): void {
    const bounds = this.objectBounds.get(objectId);
    if (!bounds) return;

    for (let level = 0; level < this.cellSizes.length; level++) {
      const cellSize = this.cellSizes[level];
      const startCellX = Math.floor(bounds.minX / cellSize);
      const endCellX = Math.floor(bounds.maxX / cellSize);
      const startCellY = Math.floor(bounds.minY / cellSize);
      const endCellY = Math.floor(bounds.maxY / cellSize);

      for (let cx = startCellX; cx <= endCellX; cx++) {
        for (let cy = startCellY; cy <= endCellY; cy++) {
          const key = `${cx},${cy}`;
          const cell = this.levels[level].get(key);
          if (cell) {
            cell.delete(objectId);
            if (cell.size === 0) {
              this.levels[level].delete(key);
            }
          }
        }
      }
    }

    this.objectBounds.delete(objectId);
  }

  /**
   * Query objects at screen position - O(1) average case
   * Uses hierarchical narrowing for efficiency
   */
  queryAtPoint(screenX: number, screenY: number, maxResults: number = 50): number[] {
    this.queryResultCount = 0;

    // Start from coarsest level and narrow down
    let candidateSet: Set<number> | null = null;

    for (let level = this.cellSizes.length - 1; level >= 0; level--) {
      const cellSize = this.cellSizes[level];
      const cellX = Math.floor(screenX / cellSize);
      const cellY = Math.floor(screenY / cellSize);
      const key = `${cellX},${cellY}`;

      const cell = this.levels[level].get(key);
      if (!cell || cell.size === 0) {
        // No objects at this level means no objects at finer levels
        return [];
      }

      if (candidateSet === null) {
        candidateSet = new Set(cell);
      } else {
        // Intersect with previous candidates
        const newCandidates = new Set<number>();
        for (const id of cell) {
          if (candidateSet.has(id)) {
            newCandidates.add(id);
          }
        }
        candidateSet = newCandidates;
      }

      // Early exit if few candidates
      if (candidateSet.size <= maxResults) {
        break;
      }
    }

    if (!candidateSet) return [];

    // Verify candidates actually contain the point and sort by depth
    const verified: Array<{ id: number; depth: number }> = [];

    for (const id of candidateSet) {
      const bounds = this.objectBounds.get(id);
      if (bounds &&
          screenX >= bounds.minX && screenX <= bounds.maxX &&
          screenY >= bounds.minY && screenY <= bounds.maxY) {
        verified.push({ id, depth: bounds.depth });
      }
    }

    // Sort by depth (closest first)
    verified.sort((a, b) => a.depth - b.depth);

    // Copy to result pool (zero allocation)
    const resultCount = Math.min(verified.length, maxResults);
    for (let i = 0; i < resultCount; i++) {
      this.queryResultPool[i] = verified[i].id;
    }
    this.queryResultCount = resultCount;

    return this.queryResultPool.slice(0, resultCount);
  }

  /**
   * Query objects in rectangular region
   */
  queryInRect(
    minX: number, minY: number,
    maxX: number, maxY: number,
    maxResults: number = 100
  ): number[] {
    const results = new Set<number>();

    // Use coarsest level for initial query
    const cellSize = this.cellSizes[this.cellSizes.length - 1];
    const startCellX = Math.floor(minX / cellSize);
    const endCellX = Math.floor(maxX / cellSize);
    const startCellY = Math.floor(minY / cellSize);
    const endCellY = Math.floor(maxY / cellSize);

    for (let cx = startCellX; cx <= endCellX; cx++) {
      for (let cy = startCellY; cy <= endCellY; cy++) {
        const key = `${cx},${cy}`;
        const cell = this.levels[this.cellSizes.length - 1].get(key);
        if (cell) {
          for (const id of cell) {
            if (results.size >= maxResults) break;

            const bounds = this.objectBounds.get(id);
            if (bounds &&
                bounds.maxX >= minX && bounds.minX <= maxX &&
                bounds.maxY >= minY && bounds.minY <= maxY) {
              results.add(id);
            }
          }
        }
      }
    }

    return Array.from(results);
  }

  /**
   * Clear entire index
   */
  clear(): void {
    for (const level of this.levels) {
      level.clear();
    }
    this.objectBounds.clear();
  }

  /**
   * Get statistics
   */
  getStats(): {
    objectCount: number;
    cellCounts: number[];
    memoryEstimate: number;
  } {
    const cellCounts = this.levels.map(level => level.size);
    const memoryEstimate = this.objectBounds.size * 48 + // bounds data
      cellCounts.reduce((a, b) => a + b, 0) * 100; // cell overhead

    return {
      objectCount: this.objectBounds.size,
      cellCounts,
      memoryEstimate
    };
  }
}

// ==================== PREDICTIVE HOVER ====================

/**
 * Predicts mouse movement and pre-computes hover results
 */
export class FikoPredictiveHover {
  private mouseHistory: Array<{ x: number; y: number; time: number }> = [];
  private maxHistoryLength: number = 10;
  private predictionHorizonMs: number = 32; // 2 frames ahead

  // Current prediction
  private currentPrediction: HoverPrediction = {
    predictedX: 0,
    predictedY: 0,
    velocity: { x: 0, y: 0 },
    confidence: 0,
    precomputedResult: null
  };

  // Pre-allocated vectors
  private velocityBuffer: { x: number; y: number }[] = [];

  constructor() {
    // Pre-allocate velocity buffer
    for (let i = 0; i < this.maxHistoryLength; i++) {
      this.velocityBuffer.push({ x: 0, y: 0 });
    }
  }

  /**
   * Update with new mouse position
   */
  updateMousePosition(x: number, y: number): void {
    const now = performance.now();

    this.mouseHistory.push({ x, y, time: now });
    if (this.mouseHistory.length > this.maxHistoryLength) {
      this.mouseHistory.shift();
    }

    this.computePrediction();
  }

  /**
   * Compute predicted position using linear regression
   */
  private computePrediction(): void {
    if (this.mouseHistory.length < 3) {
      this.currentPrediction.confidence = 0;
      return;
    }

    const now = performance.now();
    const n = this.mouseHistory.length;

    // Calculate velocities
    let avgVelX = 0;
    let avgVelY = 0;
    let velCount = 0;

    for (let i = 1; i < n; i++) {
      const dt = this.mouseHistory[i].time - this.mouseHistory[i - 1].time;
      if (dt > 0 && dt < 100) { // Ignore large gaps
        const vx = (this.mouseHistory[i].x - this.mouseHistory[i - 1].x) / dt;
        const vy = (this.mouseHistory[i].y - this.mouseHistory[i - 1].y) / dt;

        // Weight recent samples more
        const weight = (i / n) * (i / n);
        avgVelX += vx * weight;
        avgVelY += vy * weight;
        velCount += weight;
      }
    }

    if (velCount > 0) {
      avgVelX /= velCount;
      avgVelY /= velCount;
    }

    // Predict future position
    const lastPos = this.mouseHistory[n - 1];
    const timeSinceLast = now - lastPos.time;
    const predictTime = timeSinceLast + this.predictionHorizonMs;

    this.currentPrediction.predictedX = lastPos.x + avgVelX * predictTime;
    this.currentPrediction.predictedY = lastPos.y + avgVelY * predictTime;
    this.currentPrediction.velocity.x = avgVelX;
    this.currentPrediction.velocity.y = avgVelY;

    // Calculate confidence based on velocity consistency
    const speed = Math.sqrt(avgVelX * avgVelX + avgVelY * avgVelY);
    if (speed < 0.01) {
      // Mouse is stationary - high confidence
      this.currentPrediction.confidence = 1.0;
      this.currentPrediction.predictedX = lastPos.x;
      this.currentPrediction.predictedY = lastPos.y;
    } else if (speed > 2.0) {
      // Very fast movement - low confidence
      this.currentPrediction.confidence = 0.3;
    } else {
      // Normal movement
      this.currentPrediction.confidence = 0.7;
    }
  }

  /**
   * Get current prediction
   */
  getPrediction(): HoverPrediction {
    return this.currentPrediction;
  }

  /**
   * Store precomputed result
   */
  setPrecomputedResult(result: any): void {
    this.currentPrediction.precomputedResult = result;
  }

  /**
   * Check if prediction is valid for given position
   */
  isPredictionValidFor(x: number, y: number, tolerancePx: number = 15): boolean {
    const dx = x - this.currentPrediction.predictedX;
    const dy = y - this.currentPrediction.predictedY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    return dist < tolerancePx && this.currentPrediction.confidence > 0.5;
  }

  /**
   * Get mouse velocity
   */
  getVelocity(): { x: number; y: number; speed: number } {
    const speed = Math.sqrt(
      this.currentPrediction.velocity.x ** 2 +
      this.currentPrediction.velocity.y ** 2
    );
    return {
      x: this.currentPrediction.velocity.x,
      y: this.currentPrediction.velocity.y,
      speed
    };
  }

  /**
   * Should skip hover computation (mouse too fast)
   */
  shouldSkipHover(): boolean {
    const speed = Math.sqrt(
      this.currentPrediction.velocity.x ** 2 +
      this.currentPrediction.velocity.y ** 2
    );
    return speed > 1.5; // Skip if moving > 1.5px/ms
  }

  /**
   * Clear history
   */
  clear(): void {
    this.mouseHistory = [];
    this.currentPrediction = {
      predictedX: 0,
      predictedY: 0,
      velocity: { x: 0, y: 0 },
      confidence: 0,
      precomputedResult: null
    };
  }
}

// ==================== ADAPTIVE QUALITY CONTROLLER ====================

/**
 * Automatically adjusts rendering quality to maintain target FPS
 */
export class FikoAdaptiveQualityController {
  private config: PerformanceConfig;
  private frameMetricsHistory: FrameMetrics[] = [];
  private maxHistorySize: number = 60;

  // Quality adjustment state
  private lastQualityChange: number = 0;
  private qualityChangeCooldownMs: number = 1000;
  private consecutiveBadFrames: number = 0;
  private consecutiveGoodFrames: number = 0;

  // Callbacks
  private onQualityChange: ((level: QualityLevel, index: number) => void) | null = null;

  constructor(config?: Partial<PerformanceConfig>) {
    this.config = this.createDefaultConfig();
    if (config) {
      Object.assign(this.config, config);
    }
  }

  private createDefaultConfig(): PerformanceConfig {
    return {
      targetFPS: 60,
      minAcceptableFPS: 30,
      frameBudgetMs: 16.67,
      hoverBudgetMs: 3,
      renderBudgetMs: 12,

      qualityLevels: [
        // Level 0: Ultra (for small scenes)
        {
          name: 'Ultra',
          lodBias: 0,
          shadowsEnabled: true,
          ssaoEnabled: true,
          edgeRenderingQuality: 'full',
          hoverHighlightQuality: 'full',
          maxVisibleObjects: Infinity,
          frustumCullingGranularity: 'fine',
          occlusionCullingEnabled: true,
          antialiasingLevel: 8,
          textureQuality: 'full'
        },
        // Level 1: High
        {
          name: 'High',
          lodBias: 0.5,
          shadowsEnabled: true,
          ssaoEnabled: false,
          edgeRenderingQuality: 'full',
          hoverHighlightQuality: 'full',
          maxVisibleObjects: 50000,
          frustumCullingGranularity: 'fine',
          occlusionCullingEnabled: true,
          antialiasingLevel: 4,
          textureQuality: 'full'
        },
        // Level 2: Medium
        {
          name: 'Medium',
          lodBias: 1.0,
          shadowsEnabled: false,
          ssaoEnabled: false,
          edgeRenderingQuality: 'simplified',
          hoverHighlightQuality: 'fast',
          maxVisibleObjects: 20000,
          frustumCullingGranularity: 'coarse',
          occlusionCullingEnabled: true,
          antialiasingLevel: 2,
          textureQuality: 'half'
        },
        // Level 3: Low
        {
          name: 'Low',
          lodBias: 2.0,
          shadowsEnabled: false,
          ssaoEnabled: false,
          edgeRenderingQuality: 'minimal',
          hoverHighlightQuality: 'minimal',
          maxVisibleObjects: 10000,
          frustumCullingGranularity: 'aggressive',
          occlusionCullingEnabled: false,
          antialiasingLevel: 0,
          textureQuality: 'quarter'
        },
        // Level 4: Minimal (emergency)
        {
          name: 'Minimal',
          lodBias: 4.0,
          shadowsEnabled: false,
          ssaoEnabled: false,
          edgeRenderingQuality: 'off',
          hoverHighlightQuality: 'minimal',
          maxVisibleObjects: 5000,
          frustumCullingGranularity: 'aggressive',
          occlusionCullingEnabled: false,
          antialiasingLevel: 0,
          textureQuality: 'quarter'
        }
      ],
      currentQualityIndex: 1, // Start at High

      triangleCountThresholds: {
        low: 100000,
        medium: 1000000,
        high: 10000000,
        extreme: 50000000
      }
    };
  }

  /**
   * Record frame metrics and adjust quality
   */
  recordFrame(metrics: FrameMetrics): void {
    this.frameMetricsHistory.push(metrics);
    if (this.frameMetricsHistory.length > this.maxHistorySize) {
      this.frameMetricsHistory.shift();
    }

    this.evaluateQuality();
  }

  /**
   * Evaluate and adjust quality level
   */
  private evaluateQuality(): void {
    const now = performance.now();
    if (now - this.lastQualityChange < this.qualityChangeCooldownMs) {
      return; // Still in cooldown
    }

    const avgFPS = this.getAverageFPS();
    const avgFrameTime = this.getAverageFrameTime();

    // Check if we need to decrease quality
    if (avgFPS < this.config.minAcceptableFPS) {
      this.consecutiveBadFrames++;
      this.consecutiveGoodFrames = 0;

      if (this.consecutiveBadFrames >= 10) {
        this.decreaseQuality();
        this.consecutiveBadFrames = 0;
      }
    }
    // Check if we can increase quality
    else if (avgFPS > this.config.targetFPS * 0.95 && avgFrameTime < this.config.frameBudgetMs * 0.7) {
      this.consecutiveGoodFrames++;
      this.consecutiveBadFrames = 0;

      if (this.consecutiveGoodFrames >= 60) { // 1 second of good frames
        this.increaseQuality();
        this.consecutiveGoodFrames = 0;
      }
    } else {
      this.consecutiveBadFrames = 0;
      this.consecutiveGoodFrames = 0;
    }
  }

  /**
   * Decrease quality by one level
   */
  private decreaseQuality(): void {
    if (this.config.currentQualityIndex < this.config.qualityLevels.length - 1) {
      this.config.currentQualityIndex++;
      this.lastQualityChange = performance.now();

      if (this.onQualityChange) {
        this.onQualityChange(
          this.config.qualityLevels[this.config.currentQualityIndex],
          this.config.currentQualityIndex
        );
      }
    }
  }

  /**
   * Increase quality by one level
   */
  private increaseQuality(): void {
    if (this.config.currentQualityIndex > 0) {
      this.config.currentQualityIndex--;
      this.lastQualityChange = performance.now();

      if (this.onQualityChange) {
        this.onQualityChange(
          this.config.qualityLevels[this.config.currentQualityIndex],
          this.config.currentQualityIndex
        );
      }
    }
  }

  /**
   * Force quality level based on scene complexity
   */
  setQualityForSceneComplexity(triangleCount: number): void {
    let targetIndex: number;

    if (triangleCount < this.config.triangleCountThresholds.low) {
      targetIndex = 0; // Ultra
    } else if (triangleCount < this.config.triangleCountThresholds.medium) {
      targetIndex = 1; // High
    } else if (triangleCount < this.config.triangleCountThresholds.high) {
      targetIndex = 2; // Medium
    } else if (triangleCount < this.config.triangleCountThresholds.extreme) {
      targetIndex = 3; // Low
    } else {
      targetIndex = 4; // Minimal
    }

    if (targetIndex !== this.config.currentQualityIndex) {
      this.config.currentQualityIndex = targetIndex;
      this.lastQualityChange = performance.now();

      if (this.onQualityChange) {
        this.onQualityChange(
          this.config.qualityLevels[this.config.currentQualityIndex],
          this.config.currentQualityIndex
        );
      }
    }
  }

  /**
   * Get current quality level
   */
  getCurrentQuality(): QualityLevel {
    return this.config.qualityLevels[this.config.currentQualityIndex];
  }

  /**
   * Get average FPS from history
   */
  private getAverageFPS(): number {
    if (this.frameMetricsHistory.length === 0) return 60;
    const sum = this.frameMetricsHistory.reduce((a, b) => a + b.fps, 0);
    return sum / this.frameMetricsHistory.length;
  }

  /**
   * Get average frame time
   */
  private getAverageFrameTime(): number {
    if (this.frameMetricsHistory.length === 0) return 16.67;
    const sum = this.frameMetricsHistory.reduce((a, b) => a + b.frameTime, 0);
    return sum / this.frameMetricsHistory.length;
  }

  /**
   * Register quality change callback
   */
  onQualityChanged(callback: (level: QualityLevel, index: number) => void): void {
    this.onQualityChange = callback;
  }

  /**
   * Get configuration
   */
  getConfig(): PerformanceConfig {
    return this.config;
  }

  /**
   * Get statistics
   */
  getStats(): {
    currentQuality: string;
    qualityIndex: number;
    avgFPS: number;
    avgFrameTime: number;
    badFrameStreak: number;
    goodFrameStreak: number;
  } {
    return {
      currentQuality: this.getCurrentQuality().name,
      qualityIndex: this.config.currentQualityIndex,
      avgFPS: Math.round(this.getAverageFPS()),
      avgFrameTime: parseFloat(this.getAverageFrameTime().toFixed(2)),
      badFrameStreak: this.consecutiveBadFrames,
      goodFrameStreak: this.consecutiveGoodFrames
    };
  }
}

// ==================== FRAME BUDGET ENFORCER ====================

/**
 * Ensures frame budget is never exceeded
 * Uses deadline-based task scheduling
 */
export class FikoFrameBudgetEnforcer {
  private frameDeadline: number = 0;
  private frameBudgetMs: number = 16.67;
  private taskQueue: Array<{
    name: string;
    priority: number;
    execute: () => void;
    estimatedMs: number;
    critical: boolean;
  }> = [];

  // Time tracking
  private frameStartTime: number = 0;
  private usedBudget: number = 0;

  // Statistics
  private stats = {
    tasksCompleted: 0,
    tasksDeferred: 0,
    budgetOverruns: 0,
    averageUtilization: 0
  };

  constructor(frameBudgetMs: number = 16.67) {
    this.frameBudgetMs = frameBudgetMs;
  }

  /**
   * Begin frame - set deadline
   */
  beginFrame(): void {
    this.frameStartTime = performance.now();
    this.frameDeadline = this.frameStartTime + this.frameBudgetMs;
    this.usedBudget = 0;
  }

  /**
   * Check remaining budget
   */
  getRemainingBudget(): number {
    return Math.max(0, this.frameDeadline - performance.now());
  }

  /**
   * Check if budget allows operation
   */
  hasBudgetFor(estimatedMs: number): boolean {
    return this.getRemainingBudget() >= estimatedMs;
  }

  /**
   * Execute task if budget allows
   */
  executeIfBudgetAllows<T>(
    task: () => T,
    estimatedMs: number,
    fallback?: () => T
  ): T | null {
    if (this.hasBudgetFor(estimatedMs)) {
      const start = performance.now();
      const result = task();
      this.usedBudget += performance.now() - start;
      this.stats.tasksCompleted++;
      return result;
    }

    this.stats.tasksDeferred++;
    if (fallback) {
      return fallback();
    }
    return null;
  }

  /**
   * Queue task for execution
   */
  queueTask(
    name: string,
    execute: () => void,
    priority: number = 0.5,
    estimatedMs: number = 1,
    critical: boolean = false
  ): void {
    this.taskQueue.push({
      name,
      priority,
      execute,
      estimatedMs,
      critical
    });

    // Sort by priority (higher first)
    this.taskQueue.sort((a, b) => {
      if (a.critical !== b.critical) return a.critical ? -1 : 1;
      return b.priority - a.priority;
    });
  }

  /**
   * Process queued tasks within budget
   */
  processQueue(): { completed: number; deferred: number } {
    let completed = 0;
    let deferred = 0;

    const remaining: typeof this.taskQueue = [];

    for (const task of this.taskQueue) {
      if (task.critical || this.hasBudgetFor(task.estimatedMs)) {
        const start = performance.now();
        task.execute();
        this.usedBudget += performance.now() - start;
        completed++;
      } else {
        remaining.push(task);
        deferred++;
      }
    }

    this.taskQueue = remaining;
    return { completed, deferred };
  }

  /**
   * End frame - update statistics
   */
  endFrame(): { utilization: number; overrun: boolean } {
    const frameTime = performance.now() - this.frameStartTime;
    const utilization = this.usedBudget / this.frameBudgetMs;
    const overrun = frameTime > this.frameBudgetMs * 1.1;

    if (overrun) {
      this.stats.budgetOverruns++;
    }

    // Moving average for utilization
    this.stats.averageUtilization =
      this.stats.averageUtilization * 0.9 + utilization * 0.1;

    return { utilization, overrun };
  }

  /**
   * Get statistics
   */
  getStats(): typeof this.stats & { queuedTasks: number } {
    return {
      ...this.stats,
      queuedTasks: this.taskQueue.length
    };
  }

  /**
   * Clear queue
   */
  clearQueue(): void {
    this.taskQueue = [];
  }
}

// ==================== MAIN MANAGER ====================

/**
 * Central performance orchestrator
 * Coordinates all performance systems
 */
export class FikoUltraPerformanceManager {
  // Sub-systems
  public spatialIndex: FikoHierarchicalSpatialIndex;
  public predictiveHover: FikoPredictiveHover;
  public qualityController: FikoAdaptiveQualityController;
  public frameBudget: FikoFrameBudgetEnforcer;

  // Renderer reference
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.Camera | null = null;

  // Frame tracking
  private lastFrameTime: number = 0;
  private frameCount: number = 0;
  private fpsHistory: number[] = [];

  // Object tracking
  private registeredObjects: Map<number, THREE.Object3D> = new Map();
  private objectIdCounter: number = 0;

  // Statistics
  private stats = {
    totalTriangles: 0,
    visibleTriangles: 0,
    objectCount: 0,
    visibleObjectCount: 0,
    hoverHitRate: 0,
    predictionAccuracy: 0
  };

  // Singleton
  private static instance: FikoUltraPerformanceManager | null = null;

  constructor() {
    this.spatialIndex = new FikoHierarchicalSpatialIndex();
    this.predictiveHover = new FikoPredictiveHover();
    this.qualityController = new FikoAdaptiveQualityController();
    this.frameBudget = new FikoFrameBudgetEnforcer();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): FikoUltraPerformanceManager {
    if (!FikoUltraPerformanceManager.instance) {
      FikoUltraPerformanceManager.instance = new FikoUltraPerformanceManager();
    }
    return FikoUltraPerformanceManager.instance;
  }

  /**
   * Initialize with renderer
   */
  initialize(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera
  ): void {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    // Update spatial index screen size
    const canvas = renderer.domElement;
    this.spatialIndex.setScreenSize(canvas.clientWidth, canvas.clientHeight);

    // Setup quality change callback
    this.qualityController.onQualityChanged((level, index) => {
      this.applyQualityLevel(level);
    });

  }

  /**
   * Apply quality level to renderer
   */
  private applyQualityLevel(level: QualityLevel): void {
    if (!this.renderer) return;

    // Update renderer settings
    this.renderer.shadowMap.enabled = level.shadowsEnabled;

    // Update pixel ratio based on AA level
    const maxPixelRatio = window.devicePixelRatio || 1;
    if (level.antialiasingLevel === 0) {
      this.renderer.setPixelRatio(1);
    } else {
      this.renderer.setPixelRatio(Math.min(maxPixelRatio, level.antialiasingLevel / 4));
    }

  }

  /**
   * Register object for tracking
   */
  registerObject(object: THREE.Object3D): number {
    const id = this.objectIdCounter++;
    this.registeredObjects.set(id, object);
    this.stats.objectCount++;
    return id;
  }

  /**
   * Unregister object
   */
  unregisterObject(id: number): void {
    this.registeredObjects.delete(id);
    this.spatialIndex.removeObject(id);
    this.stats.objectCount--;
  }

  /**
   * Update spatial index for visible objects
   */
  updateSpatialIndex(): void {
    if (!this.renderer || !this.camera) return;

    const canvas = this.renderer.domElement;
    this.spatialIndex.setScreenSize(canvas.clientWidth, canvas.clientHeight);

    // Project objects to screen space
    const tempVec = new THREE.Vector3();

    for (const [id, object] of this.registeredObjects) {
      if (!object.visible) continue;

      // Get bounding box
      const box = new THREE.Box3().setFromObject(object);
      if (box.isEmpty()) continue;

      // Project corners to screen
      const corners = [
        new THREE.Vector3(box.min.x, box.min.y, box.min.z),
        new THREE.Vector3(box.max.x, box.min.y, box.min.z),
        new THREE.Vector3(box.min.x, box.max.y, box.min.z),
        new THREE.Vector3(box.max.x, box.max.y, box.min.z),
        new THREE.Vector3(box.min.x, box.min.y, box.max.z),
        new THREE.Vector3(box.max.x, box.min.y, box.max.z),
        new THREE.Vector3(box.min.x, box.max.y, box.max.z),
        new THREE.Vector3(box.max.x, box.max.y, box.max.z)
      ];

      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;
      let avgDepth = 0;

      for (const corner of corners) {
        corner.project(this.camera);
        const screenX = (corner.x + 1) / 2 * canvas.clientWidth;
        const screenY = (-corner.y + 1) / 2 * canvas.clientHeight;

        minX = Math.min(minX, screenX);
        maxX = Math.max(maxX, screenX);
        minY = Math.min(minY, screenY);
        maxY = Math.max(maxY, screenY);
        avgDepth += corner.z;
      }

      avgDepth /= 8;

      this.spatialIndex.addObject(id, minX, minY, maxX, maxY, avgDepth);
    }
  }

  /**
   * Process mouse move for hover
   */
  processMouseMove(screenX: number, screenY: number): number[] {
    // Update predictive hover
    this.predictiveHover.updateMousePosition(screenX, screenY);

    // Skip if moving too fast
    if (this.predictiveHover.shouldSkipHover()) {
      return [];
    }

    // Check if prediction is valid
    const prediction = this.predictiveHover.getPrediction();
    if (
      prediction.precomputedResult &&
      this.predictiveHover.isPredictionValidFor(screenX, screenY)
    ) {
      return prediction.precomputedResult;
    }

    // Query spatial index
    const candidates = this.spatialIndex.queryAtPoint(screenX, screenY);

    // Pre-compute for predicted position
    const predictedCandidates = this.spatialIndex.queryAtPoint(
      prediction.predictedX,
      prediction.predictedY
    );
    this.predictiveHover.setPrecomputedResult(predictedCandidates);

    return candidates;
  }

  /**
   * Begin frame
   */
  beginFrame(): void {
    this.frameBudget.beginFrame();
    this.lastFrameTime = performance.now();
  }

  /**
   * End frame and record metrics
   */
  endFrame(): void {
    const frameTime = performance.now() - this.lastFrameTime;
    const fps = 1000 / Math.max(frameTime, 0.001);

    this.fpsHistory.push(fps);
    if (this.fpsHistory.length > 60) {
      this.fpsHistory.shift();
    }

    // Record metrics
    const metrics: FrameMetrics = {
      frameTime,
      fps,
      renderTime: frameTime * 0.7, // Estimate
      hoverTime: 0,
      cullingTime: 0,
      updateTime: 0,
      gpuTime: 0,
      trianglesRendered: this.stats.visibleTriangles,
      drawCalls: this.renderer?.info.render.calls || 0,
      objectsVisible: this.stats.visibleObjectCount,
      objectsCulled: this.stats.objectCount - this.stats.visibleObjectCount
    };

    this.qualityController.recordFrame(metrics);
    this.frameBudget.endFrame();
    this.frameCount++;
  }

  /**
   * Update scene statistics
   */
  updateSceneStats(triangleCount: number, visibleCount?: number): void {
    this.stats.totalTriangles = triangleCount;
    this.stats.visibleTriangles = visibleCount ?? triangleCount;

    // Auto-adjust quality based on triangle count
    this.qualityController.setQualityForSceneComplexity(triangleCount);
  }

  /**
   * Get current FPS
   */
  getCurrentFPS(): number {
    if (this.fpsHistory.length === 0) return 60;
    return this.fpsHistory.reduce((a, b) => a + b, 0) / this.fpsHistory.length;
  }

  /**
   * Check if budget allows hover computation
   */
  canDoHover(): boolean {
    const quality = this.qualityController.getCurrentQuality();
    const hoverBudget = quality.hoverHighlightQuality === 'minimal' ? 1 : 3;
    return this.frameBudget.hasBudgetFor(hoverBudget);
  }

  /**
   * Get comprehensive statistics
   */
  getStats(): {
    fps: number;
    quality: string;
    triangles: { total: number; visible: number };
    objects: { total: number; visible: number };
    spatialIndex: ReturnType<FikoHierarchicalSpatialIndex['getStats']>;
    frameBudget: ReturnType<FikoFrameBudgetEnforcer['getStats']>;
    qualityController: ReturnType<FikoAdaptiveQualityController['getStats']>;
    mouseVelocity: number;
  } {
    return {
      fps: Math.round(this.getCurrentFPS()),
      quality: this.qualityController.getCurrentQuality().name,
      triangles: {
        total: this.stats.totalTriangles,
        visible: this.stats.visibleTriangles
      },
      objects: {
        total: this.stats.objectCount,
        visible: this.stats.visibleObjectCount
      },
      spatialIndex: this.spatialIndex.getStats(),
      frameBudget: this.frameBudget.getStats(),
      qualityController: this.qualityController.getStats(),
      mouseVelocity: this.predictiveHover.getVelocity().speed
    };
  }

  /**
   * Dispose
   */
  dispose(): void {
    this.spatialIndex.clear();
    this.predictiveHover.clear();
    this.registeredObjects.clear();
    FikoUltraPerformanceManager.instance = null;
  }
}

// Export default instance getter
export const getPerformanceManager = FikoUltraPerformanceManager.getInstance;
